import { FocusContext, setFocus, useFocusable } from '@noriginmedia/norigin-spatial-navigation'
import { useCallback, useEffect, useRef, useState } from 'react'
import { StreamLayerSDKTv, type VideoPlayerCallback, useStreamLayer } from '@lib/es'

import {
  ContentWrapper,
  VideoContainer,
  BottomBar,
  VideoInfo,
  LiveChannelLabel,
  VideoTitle,
  PlayPauseButton,
  VideoWrapper,
  Video,
} from '../styles'
import type { Channel } from '../app/ChannelsList'

interface VideoViewProps {
  channel: Channel
}

const VIDEO_VIEW_FOCUS_KEY = 'VIDEO_VIEW'
const VIDEO_PLAY_PAUSE_FOCUS_KEY = 'VIDEO_PLAY_PAUSE_BUTTON'

const isStream = (src: string) => {
  return !src || src.includes('m3u8') || src.includes('webm') || src.includes('ogg')
}

export const VideoView: React.FC<VideoViewProps> = ({ channel: fallbackChannel }) => {
  const sdk = useStreamLayer()
  const [channel, setChannel] = useState<Channel>()
  const [showPauseAd, setShowPauseAd] = useState(false)
  const [pauseAdRendered, setPauseAdRendered] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const videoPlayerRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (sdk) {
      return sdk.streamSummary().subscribe((value: any) => {
        if (value.data?.summary && (value.data.summary.stream || value.data.summary.pauseAdUrl?.[0])) {
          setChannel({
            id: value.data.summary.id,
            name: value.data.summary.name,
            videoStreamUrl: isStream(value.data.summary.stream)
              ? fallbackChannel.videoStreamUrl
              : value.data.summary.stream,
            vastUrl: value.data.summary.pauseAdUrl[0],
            duration: '',
            thumbnail: value.data.summary.logo || '',
          })
        } else {
          setChannel(fallbackChannel)
        }
      })
    }
  }, [sdk])

  const onClosePauseAd = useCallback(() => {
    setShowPauseAd(false)
  }, [])

  const onRenderPauseAd = useCallback((params: { rendered: boolean }) => {
    setPauseAdRendered(params.rendered)
  }, [])

  // Handle Media Play
  const handleMediaPlay = useCallback(() => {
    videoPlayerRef.current?.play()
  }, [])

  // Handle Media Pause
  const handleMediaPause = useCallback(() => {
    videoPlayerRef.current?.pause()
  }, [])

  // Handle Enter key - toggle play/pause
  const handleSelect = useCallback(() => {
    if (videoPlayerRef.current?.paused) {
      videoPlayerRef.current?.play()
    } else {
      videoPlayerRef.current?.pause()
    }
  }, [])

  const showControls = !pauseAdRendered

  const clearFocusRef = useRef<NodeJS.Timeout | null>(null)

  const { ref: playPauseButtonRef, focusSelf: focusPlayPauseSelf } = useFocusable({
    focusKey: VIDEO_PLAY_PAUSE_FOCUS_KEY,
    onEnterPress: () => handleSelect(),
    onFocus: () => {
      if (clearFocusRef.current) {
        clearTimeout(clearFocusRef.current)
      }
      clearFocusRef.current = setTimeout(() => {
        playPauseButtonRef.current?.blur()
      }, 5000)
    },
    onBlur: () => {
      if (clearFocusRef.current) {
        clearTimeout(clearFocusRef.current)
      }
    },
  })

  const { ref: videoViewRef, focusKey: videoViewFocusKey } = useFocusable({
    focusKey: VIDEO_VIEW_FOCUS_KEY,
    focusable: false,
    onArrowPress: () => {
      focusPlayPauseSelf()

      return false
    },
  })

  // Ensure focus starts on Play/Pause when the controls are visible.
  useEffect(() => {
    setFocus(VIDEO_PLAY_PAUSE_FOCUS_KEY)
  }, [])

  // Keep WebOS/Tizen special keys working (and don't let spatial-nav consume them).
  useEffect(() => {
    const onKeyDownCapture = (e: KeyboardEvent) => {
      const code = e.keyCode

      // Back (WebOS/Tizen) + Escape
      if (code === 461 || code === 10009 || code === 27) {
        e.preventDefault()
        e.stopPropagation()

        return
      }

      // Media play/pause (some remotes send these)
      if (code === 415) {
        e.preventDefault()
        e.stopPropagation()
        handleMediaPlay()

        return
      }

      if (code === 19) {
        e.preventDefault()
        e.stopPropagation()
        handleMediaPause()

        return
      }

      // tizen play/pause, webos play/pause
      if (code === 10252 || code === 503) {
        e.preventDefault()
        e.stopPropagation()
        handleSelect()

        return
      }
    }

    window.addEventListener('keydown', onKeyDownCapture)
    return () => window.removeEventListener('keydown', onKeyDownCapture)
  }, [handleMediaPlay, handleMediaPause])

  // Track video playing state
  const onVideoPlayWithState = useCallback(() => {
    setShowPauseAd(false)
    setIsPlaying(true)
  }, [])

  const onVideoPauseWithState = useCallback(() => {
    setShowPauseAd(true)
    setIsPlaying(false)
  }, [])

  const videoPlayerController: VideoPlayerCallback = useCallback((videoPlayerData) => {
    const video = videoPlayerRef.current

    if (!video) return

    if (videoPlayerData.play === true) {
      setShowPauseAd(false)

      video.play()
    }

    if (typeof videoPlayerData.muted === 'boolean') {
      video.muted = videoPlayerData.muted
    }
  }, [])

  if (!channel) {
    return <div>no channel</div>
  }

  return (
    <FocusContext.Provider value={videoViewFocusKey}>
      <StreamLayerSDKTv
        showPauseAd={showPauseAd}
        onRenderPauseAd={onRenderPauseAd}
        onClosePauseAd={onClosePauseAd}
        videoPlayerController={videoPlayerController}
        options={{
          pauseAdDelay: 0,
          pauseAdRefetchInterval: 1000 * 60 * 5, // 5 minutes
        }}
        pauseAdVastUrl={[
          {
            template: 'default',
            url: channel.vastUrl,
          },
        ]}
      >
        <ContentWrapper ref={videoViewRef}>
          <VideoContainer>
            <VideoWrapper>
              <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                <Video
                  ref={videoPlayerRef}
                  src={channel.videoStreamUrl}
                  onPlay={onVideoPlayWithState}
                  onPause={onVideoPauseWithState}
                  style={{ width: '100%', objectFit: 'contain' }}
                  autoPlay
                  data-testid="video-frame"
                  loop
                  playsInline
                  controls={false}
                  controlsList="nodownload nofullscreen noremoteplayback"
                />
              </div>
            </VideoWrapper>
            <PlayPauseButton
              ref={playPauseButtonRef}
              type="button"
              style={{
                visibility: !showControls ? 'hidden' : 'visible',
              }}
              aria-label="Play/Pause"
              onClick={handleSelect}
              className="material-symbols-outlined"
            >
              {isPlaying ? 'pause_circle' : 'play_circle'}
            </PlayPauseButton>
            {showControls && (
              <BottomBar>
                <VideoInfo>
                  <LiveChannelLabel>LIVE CHANNEL</LiveChannelLabel>
                  <VideoTitle>{channel.name}</VideoTitle>
                </VideoInfo>
              </BottomBar>
            )}
          </VideoContainer>
        </ContentWrapper>
      </StreamLayerSDKTv>
    </FocusContext.Provider>
  )
}
